import { ACCESS, type Access } from '@/features/access/access';
import type { ComponentType } from 'react';
import * as Icon from '@/icons/Icon';
import type { SettingsSection } from './search-schema';

/**
 * Per-section header metadata — title, scope, and a one-line description —
 * matching the redesign mockup's `sec-head` (title + scope pill + blurb)
 * shown above every section's content. Non-component module so
 * `SectionHeader.tsx` stays a Fast Refresh boundary.
 */
export type SectionScope = 'account' | 'workspace' | 'org';

export type SectionMeta = {
  title: string;
  scope: SectionScope;
  description: string;
  /** Danger zone only — renders the title in the destructive color. */
  danger?: boolean;
};

export const SECTION_META: Record<SettingsSection, SectionMeta> = {
  profile: {
    title: 'Profile',
    scope: 'account',
    description:
      'Your name and photo. You can also reach this from your avatar at the bottom of the sidebar.',
  },
  notifications: {
    title: 'Notifications',
    scope: 'account',
    description: 'Which alerts reach you, and where. This only changes your own inbox.',
  },
  security: {
    title: 'Security',
    scope: 'account',
    description:
      'Devices that are signed in as you. Sign out any you don’t recognize, or sign out everywhere at once.',
  },
  general: {
    title: 'General',
    scope: 'workspace',
    description: 'The name and daily sending quota for the workspace you’re in.',
  },
  access: {
    title: 'Workspace access',
    scope: 'workspace',
    description: 'Who can see and change the mailboxes in this workspace.',
  },
  danger: {
    title: 'Danger zone',
    scope: 'account',
    description: 'Leave this workspace, or sign out everywhere. These can’t be undone.',
    danger: true,
  },
  company: {
    title: 'Company',
    scope: 'org',
    description: 'Your organization’s timezone, owners, and plan. Only owners can open this.',
  },
  people: {
    title: 'People',
    scope: 'org',
    description: 'Everyone in the organization, their role, and which workspaces they can reach.',
  },
  workspaces: {
    title: 'Workspaces',
    scope: 'org',
    description:
      'Separate mailbox pools. Each one gets a share of the plan’s daily sending quota, and only owners can change the split.',
  },
};

/**
 * Per-section icon — the single source `SettingsRail`'s rail and the command
 * palette's registry both read from, so the two glyph sets can't drift apart
 * (they briefly did: the palette shipped with one generic Settings gear on
 * every row before this existed).
 */
export const SECTION_ICON: Record<SettingsSection, ComponentType<{ size?: number }>> = {
  profile: Icon.Users,
  notifications: Icon.Bell,
  security: Icon.Shield,
  danger: Icon.Warn,
  general: Icon.Settings,
  access: Icon.Users,
  company: Icon.Building,
  people: Icon.Users,
  workspaces: Icon.Layers,
};

/**
 * The (Feature, Op) each section's data needs, from the shared role map. A section
 * the role can't use is not rendered: absent from `SettingsRail` and the command
 * palette, and a deep link to it redirects (`routes/settings.tsx`). Single source
 * for all three so they can't drift on who can reach which section. No entry = no
 * role requirement (Profile, Notifications, Security, Danger zone are account-level).
 *
 * `company`/`people` read the org members list (OWNER); `general` reads the org
 * (ADMIN — Editor/Viewer lose it, per the redesign).
 */
export const SECTION_ACCESS: Partial<Record<SettingsSection, Access>> = {
  company: ACCESS.orgMembers,
  people: ACCESS.orgMembers,
  general: ACCESS.orgRead,
  workspaces: ACCESS.workspaces,
  access: ACCESS.team,
};

/** `workspace` is filled in with the actual current workspace name at the call site. */
export function scopeLabel(scope: SectionScope, workspaceName: string | null): string {
  switch (scope) {
    case 'account':
      return 'Only you';
    case 'workspace':
      return workspaceName ? `${workspaceName} only` : 'This workspace only';
    case 'org':
      return 'All workspaces';
  }
}
